import { useEffect, useMemo, useState } from 'react';
import { Modal } from '@mantine/core';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Icon } from './Icon';
import { useEntrepreneursSearch } from '@/hooks/useEntrepreneurs';
import { formatPhone } from '@/lib/format';
import type { Entrepreneur } from '@/api/entrepreneurs';

type Props = {
  opened: boolean;
  onClose: () => void;
};

/**
 * Global ⌘K palette. Mounted once at the AppShell level so it survives
 * route changes; opens on Cmd/Ctrl+K from anywhere. Searches the
 * entrepreneur projection live and lets the operator open a fiche
 * directly from the result list (Enter or click).
 *
 * The palette also doubles as a quick-jump for direct phone numbers —
 * typing a complete +225 number jumps to /dossiers/$phone even if the
 * search hasn't returned anything yet.
 */
export function CommandPalette({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [highlight, setHighlight] = useState(0);

  // Reset state when the palette opens — so the operator always lands
  // on an empty search.
  useEffect(() => {
    if (opened) {
      setQ('');
      setHighlight(0);
    }
  }, [opened]);

  const isPhone = /^\+\d{6,}$/.test(q.trim());
  const search = useEntrepreneursSearch({
    name: !isPhone && q.length >= 2 ? q : undefined,
    phone: isPhone ? q.trim() : undefined,
    limit: 10,
  });

  const items = useMemo(() => {
    if (q.length < 2) return [];
    return (search.data?.items ?? []).slice(0, 10);
  }, [q, search.data]);

  function go(e: Entrepreneur) {
    onClose();
    void navigate({ to: '/dossiers/$phone', params: { phone: e.phoneNumber } });
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(items.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (items[highlight]) {
        go(items[highlight]);
      } else if (isPhone) {
        onClose();
        void navigate({ to: '/dossiers/$phone', params: { phone: q.trim() } });
      }
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      withCloseButton={false}
      size={620}
      padding={0}
      centered
      overlayProps={{ blur: 2 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--line)' }}>
        <Icon name="search" size={18} style={{ color: 'var(--ink-3)' }} />
        <input
          autoFocus
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setHighlight(0);
          }}
          onKeyDown={handleKey}
          placeholder={t('search.placeholder') ?? ''}
          style={{
            flex: 1,
            border: 'none',
            background: 'none',
            outline: 'none',
            font: '600 14.5px var(--ff)',
            color: 'var(--ink)',
          }}
        />
        <span
          style={{
            font: '700 11px/1 var(--mono)',
            color: 'var(--ink-4)',
            background: 'var(--panel-2)',
            border: '1px solid var(--line)',
            borderRadius: 6,
            padding: '4px 6px',
          }}
        >
          Esc
        </span>
      </div>

      <div style={{ maxHeight: 420, overflowY: 'auto' }}>
        {q.length < 2 && (
          <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
            {t('search.hint')}
          </div>
        )}

        {q.length >= 2 && items.length === 0 && !search.isLoading && (
          <div className="muted" style={{ padding: 24, textAlign: 'center', fontSize: 13 }}>
            {t('search.noResults')}
          </div>
        )}

        {items.map((e, i) => (
          <button
            key={e.phoneNumber}
            onClick={() => go(e)}
            onMouseEnter={() => setHighlight(i)}
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              width: '100%',
              textAlign: 'left',
              padding: '12px 18px',
              background: i === highlight ? 'var(--accent-tint)' : 'transparent',
              border: 'none',
              borderBottom: '1px solid var(--line)',
              fontFamily: 'var(--ff)',
              cursor: 'pointer',
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: 'var(--accent-tint-2)',
                color: 'var(--accent-ink)',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 800,
                fontSize: 12,
                flex: '0 0 auto',
              }}
            >
              {(e.displayName ?? e.businessName ?? e.phoneNumber).slice(0, 2).toUpperCase()}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontWeight: 700, fontSize: 13.5 }}>
                {e.displayName ?? e.businessName ?? '— anonyme'}
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  fontWeight: 500,
                  fontFamily: 'var(--mono)',
                  marginTop: 2,
                }}
              >
                {formatPhone(e.phoneNumber)} {e.commune ? `· ${e.commune}` : ''}
              </span>
            </span>
            <span
              style={{
                font: '700 11px/1 var(--mono)',
                color: 'var(--ink-4)',
                background: 'var(--panel-2)',
                border: '1px solid var(--line)',
                borderRadius: 6,
                padding: '4px 8px',
              }}
            >
              {t('search.open')}
            </span>
          </button>
        ))}
      </div>
    </Modal>
  );
}
